import { Box, Fade, Icon, LinearProgress, Stack, Typography } from "@mui/material";
import React from "react";
import {
  useCreateMonoovaAccountMutation,
  useGetMonoovaAccountDetailsQuery,
  useGetUserDatav2Query,
} from "src/store/features/api/generated";
import BootstrapButton from "src/components/banking/BootstrapButton";
import CalebAndBrown from "src/icons/CalebAndBrown";

function GenerateAccount() {
  // state
  // progress state indicates progression of api call to user
  // 0 is initial state
  // 1 is first progression
  // 2 is second progression
  // 5 transition state
  const [progress, setProgress] = React.useState(0);
  const [showLoadingBar, setShowLoadingBar] = React.useState(false);
  const [buttonClicked, setButtonClicked] = React.useState(false);
  const [isCreateAccountSuccess, setIsCreateAccountSuccess] = React.useState<boolean | undefined>();

  // redux
  const [createAccount] = useCreateMonoovaAccountMutation();
  const { refetch } = useGetMonoovaAccountDetailsQuery();
  const { data } = useGetUserDatav2Query();

  // const
  const generateAccMsg = `Generating your unique payment account...`;
  const generatingMsg = `Generating PayID...`;
  const successMsg = `Success!!`;
  const errorMsg = `An error occurred when generating the your account. Please
  contact the Caleb & Brown service desk for assistance.`;

  // functions
  const handleClick = async () => {
    setButtonClicked(true);
    setProgress(5);

    // make api call
    await createAccount()
      .unwrap()
      .then((res) => {
        setShowLoadingBar(false);
        setTimeout(() => {
          setIsCreateAccountSuccess(true);
        }, 800);
      })
      .catch((e) => {
        setShowLoadingBar(false);
        setTimeout(() => {
          setIsCreateAccountSuccess(false);
        }, 800);
      });

    return;
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "600px",
        mx: { lg: "300px", md: "200px", sm: "50px", xs: "20px" },
        mt: 4,
      }}>
      <Box
        sx={
          {
            // position: "absolute",
            // background: "white",
            // height: "100%",
            // width: "80%",
            // zIndex: "1000000000",
          }
        }>
        <Stack
          spacing={2}
          direction="column"
          sx={
            {
              // position: "relative",
              // left: "50%",
              // top: "40%",
              // transform: "translate(-50%, -50%)",
              // maxWidth: "400px",
              // textAlign: "center",
              // alignItems: "center",
            }
          }>
          <Box sx={{ position: "relative", width: "100%" }}>
            {/* Step 0 */}
            <Fade
              in={progress === 0}
              easing={{ exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
              timeout={500}
              unmountOnExit
              onExited={() => {
                setProgress(1);
                setShowLoadingBar(true);
              }}>
              <Stack spacing={2} direction="column" sx={{ width: "100%", alignItems: "center" }}>
                <Icon component={CalebAndBrown} />
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  Hi {data?.details.name.split(" ")[0]}!
                </Typography>
                <Typography variant="body1">
                  We've noticed you have yet to set up a payment account with us. Please click the{" "}
                  <strong>"Generate account"</strong> button and begin depositing money via PayID or
                  bank transfer.
                </Typography>
                <BootstrapButton onClick={handleClick} disabled={buttonClicked}>
                  Generate Account
                </BootstrapButton>
              </Stack>
            </Fade>
            {/* Step 1 */}
            <Fade
              in={showLoadingBar && progress === 1}
              easing={{ exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
              timeout={500}
              mountOnEnter
              unmountOnExit
              onEntered={() => setTimeout(() => setProgress(5), 3000)}
              onExited={() => setProgress(2)}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {generateAccMsg}
                </Typography>
              </Box>
            </Fade>
            {/* Step 2 */}
            <Fade
              in={showLoadingBar && progress === 2}
              easing={{ exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
              timeout={500}
              mountOnEnter
              unmountOnExit>
              <Box sx={{ width: "100%" }}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {generatingMsg}
                </Typography>
              </Box>
            </Fade>

            <Fade
              in={showLoadingBar}
              easing={{ exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
              timeout={500}
              mountOnEnter
              unmountOnExit>
              <Box sx={{ width: "100%" }} my={2}>
                <LinearProgress />
              </Box>
            </Fade>

            {/* On Success */}
            <Fade
              in={isCreateAccountSuccess === true}
              easing={{ exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
              timeout={500}
              onEntered={() => setTimeout(() => refetch(), 1000)}
              mountOnEnter
              unmountOnExit>
              <Stack spacing={1} sx={{ width: "100%", alignItems: "center" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    border: "solid 2px black",
                    borderRadius: "50%",
                    width: "2.5rem",
                    height: "2.5rem",
                  }}>
                  &#10003;
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {successMsg}
                </Typography>
              </Stack>
            </Fade>
            {/* On Failure */}
            <Fade
              in={isCreateAccountSuccess === false}
              easing={{ exit: "cubic-bezier(0.4, 0, 0.2, 1)" }}
              timeout={500}
              onEntered={() => setTimeout(() => setProgress(6), 10000)}
              onExited={() => refetch()}
              mountOnEnter
              unmountOnExit>
              <Stack spacing={1} sx={{ width: "100%", alignItems: "center" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.5rem",
                    border: "solid 2px black",
                    borderRadius: "50%",
                    width: "2.5rem",
                    height: "2.5rem",
                  }}>
                  !
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {errorMsg}
                </Typography>
              </Stack>
            </Fade>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

export default GenerateAccount;
